import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleAuthError } from '../utils/auth';

const AccountPage = () => {
    const [userInfo, setUserInfo] = useState({
        firstName: '',
        lastName: '',
        email: '',
    });
    const [passwordFields, setPasswordFields] = useState({
        oldPassword: '',
        newPassword: '',
        confirmNewPassword: '',
    });
    const [isEditing, setIsEditing] = useState(false);
    const [message, setMessage] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        fetchAccountInfo();
    }, []);

    const fetchAccountInfo = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                handleAuthError(navigate);
                return;
            }
            const response = await fetch('https://best3intennis.com/best3intennis-api/api/get_account_info.php', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) {
                const data = await response.json().catch(() => null);
                if (data && (data.message?.includes('token') || data.error?.includes('token'))) {
                    handleAuthError(navigate);
                    return;
                }
                throw new Error(data?.message || `HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            if (data.success) {
                setUserInfo({ firstName: data.first_name, lastName: data.last_name, email: data.email });
            } else {
                if (data.message?.includes('token') || data.error?.includes('token')) {
                    handleAuthError(navigate);
                } else {
                    setMessage(data.message || 'Failed to fetch account info.');
                }
            }
        } catch (error) {
            console.error('Error fetching account info:', error);
            if (error.message.includes('token')) {
                handleAuthError(navigate);
            } else {
                setMessage('An error occurred while fetching account info.');
            }
        }
    };

    const handleUserInfoChange = (e) => {
        setUserInfo({ ...userInfo, [e.target.name]: e.target.value });
    };

    const handlePasswordChange = (e) => {
        setPasswordFields({ ...passwordFields, [e.target.name]: e.target.value });
    };

    const handleSave = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                handleAuthError(navigate);
                return;
            }
            const response = await fetch('https://best3intennis.com/best3intennis-api/api/update_account_info.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ firstName: userInfo.firstName, lastName: userInfo.lastName })
            });
            if (!response.ok) {
                const data = await response.json().catch(() => null);
                if (data && (data.message?.includes('token') || data.error?.includes('token'))) {
                    handleAuthError(navigate);
                    return;
                }
                throw new Error(data?.message || `HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            if (data.success) {
                setMessage('Account information updated successfully!');
                setIsEditing(false);
                fetchAccountInfo(); // Refresh account info after successful update
            } else {
                if (data.message?.includes('token') || data.error?.includes('token')) {
                    handleAuthError(navigate);
                } else {
                    setMessage(data.message || 'Failed to update account information.');
                }
            }
        } catch (error) {
            console.error('Error updating account info:', error);
            if (error.message.includes('token')) {
                handleAuthError(navigate);
            } else {
                setMessage('An error occurred while updating account info.');
            }
        }
    };

    const handlePasswordUpdate = async () => {
        // Clear previous messages
        setMessage('');

        if (passwordFields.newPassword !== passwordFields.confirmNewPassword) {
            setMessage('New password and confirm new password do not match.');
            return;
        }

        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{6,}$/;
        if (!passwordRegex.test(passwordFields.newPassword)) {
            setMessage('New password must be at least 6 characters long, contain at least one uppercase letter, one lowercase letter, and one number.');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                handleAuthError(navigate);
                return;
            }
            const response = await fetch('https://best3intennis.com/best3intennis-api/api/change_password.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    oldPassword: passwordFields.oldPassword,
                    newPassword: passwordFields.newPassword
                })
            });
            if (!response.ok) {
                const data = await response.json().catch(() => null);
                if (data && (data.message?.includes('token') || data.error?.includes('token'))) {
                    handleAuthError(navigate);
                    return;
                }
                throw new Error(data?.message || `HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            if (data.success) {
                setMessage('Password updated successfully!');
                setPasswordFields({ oldPassword: '', newPassword: '', confirmNewPassword: '' });
            } else {
                if (data.message?.includes('token') || data.error?.includes('token')) {
                    handleAuthError(navigate);
                } else {
                    setMessage(data.message || 'Failed to update password.');
                }
            }
        } catch (error) {
            console.error('Error changing password:', error);
            if (error.message.includes('token')) {
                handleAuthError(navigate);
            } else {
                setMessage('An error occurred while changing password.');
            }
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        fetchAccountInfo(); // Revert changes
        navigate('/dashboard');
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-6 max-w-2xl mx-auto my-8">
            <h2 className="text-2xl font-semibold text-green-800 mb-6 text-center">Account Information</h2>
            {message && <p className="bg-green-100 text-green-700 border border-green-200 p-3 mb-4 rounded-md text-center text-sm">{message}</p>}
            
            <div className="space-y-4 mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <label className="font-semibold text-gray-700 w-32 shrink-0">First Name:</label>
                    {isEditing ? (
                        <input
                            type="text"
                            name="firstName"
                            value={userInfo.firstName}
                            onChange={handleUserInfoChange}
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
                        />
                    ) : (
                        <span className="flex-grow p-2 border border-gray-300 bg-gray-100 rounded-md text-gray-700">{userInfo.firstName}</span>
                    )}
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <label className="font-semibold text-gray-700 w-32 shrink-0">Last Name:</label>
                    {isEditing ? (
                        <input
                            type="text"
                            name="lastName"
                            value={userInfo.lastName}
                            onChange={handleUserInfoChange}
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
                        />
                    ) : (
                        <span className="flex-grow p-2 border border-gray-300 bg-gray-100 rounded-md text-gray-700">{userInfo.lastName}</span>
                    )}
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <label className="font-semibold text-gray-700 w-32 shrink-0">Email:</label>
                    <span className="flex-grow p-2 border border-gray-300 bg-gray-100 rounded-md text-gray-700">{userInfo.email}</span> {/* Email cannot be changed */}
                </div>
            </div>

            <div className="flex justify-center gap-4 mt-6 mb-8 flex-wrap">
                {isEditing ? (
                    <>
                        <button onClick={handleSave} className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-md transition duration-300">Save</button>
                        <button onClick={handleCancel} className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-6 rounded-md transition duration-300">Cancel</button>
                    </>
                ) : (
                    <button onClick={() => setIsEditing(true)} className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-md transition duration-300">Edit Account Info</button>
                )}
            </div>

            <h3 className="text-xl font-semibold text-green-800 mb-4 text-center border-t pt-6 mt-6 border-gray-200">Change Password</h3>
            <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <label htmlFor="oldPassword" className="font-semibold text-gray-700 w-32 shrink-0">Old Password:</label>
                    <input
                        type="password"
                        id="oldPassword"
                        name="oldPassword"
                        value={passwordFields.oldPassword}
                        onChange={handlePasswordChange}
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
                    />
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <label htmlFor="newPassword" className="font-semibold text-gray-700 w-32 shrink-0">New Password:</label>
                    <input
                        type="password"
                        id="newPassword"
                        name="newPassword"
                        value={passwordFields.newPassword}
                        onChange={handlePasswordChange}
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
                    />
                    {passwordFields.newPassword && !passwordFields.newPassword.match(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{6,}$/) && (
                        <p className="text-red-500 text-xs italic mt-1">
                            Password must be at least 6 characters long, contain at least one uppercase letter, one lowercase letter, and one number.
                        </p>
                    )}
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <label htmlFor="confirmNewPassword" className="font-semibold text-gray-700 w-32 shrink-0">Confirm New Password:</label>
                    <input
                        type="password"
                        id="confirmNewPassword"
                        name="confirmNewPassword"
                        value={passwordFields.confirmNewPassword}
                        onChange={handlePasswordChange}
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500"
                    />
                    {passwordFields.confirmNewPassword && passwordFields.newPassword !== passwordFields.confirmNewPassword && (
                        <p className="text-red-500 text-xs italic mt-1">Passwords do not match.</p>
                    )}
                    {passwordFields.confirmNewPassword && passwordFields.newPassword === passwordFields.confirmNewPassword && (
                        <span className="text-green-500 text-xs italic mt-1">Passwords match!</span>
                    )}
                </div>
                <button onClick={handlePasswordUpdate} className="bg-yellow-400 hover:bg-yellow-500 text-green-800 font-semibold py-2 px-6 rounded-md transition duration-300 block w-fit mx-auto mt-6">Change Password</button>
            </div>
        </div>
    );
};

export default AccountPage;


